/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect } from 'react';
import styles from './AddLocationModal.module.css';
import { FloComponentProps } from 'components';
import { FloModal } from 'components';
import { Formik } from 'formik';
import * as Yup from 'yup';
import Message from 'containers/common/Message';
import { Form, Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { FloFormGroup, FloDropdown, FloModalBodyProps, FloFormControl } from 'components';
import { Country, Region, LocationType, Timezone, Location } from 'model';
import { ListView } from 'model/state';
import { FloDropdownOption } from 'components/FloDropdown';
import { uniqBy } from 'lodash';


interface AddLocationBodyProps extends FloModalBodyProps {
  isSubmitting:       boolean;
  accountId?:         string;
  countries?:         Country[];
  fetchRegionList:    (countryId: string) => void;
  fetchTimezoneList:  (countryId: string, stateId: string | null) => void;
  regions?:           Region[];
  timezones?:         Timezone[];
  type:               LocationType;
  crateLocation:      (location: Location) => void;
  editLocation:       (location: Location) => void;
  userLocations:      ListView<Location>;
  parent:             Location | null;
  currentLocation:    Location | null;
  fetchUserLocations: (userId: string, types: LocationType[], page: number, search?: string) => void;
  userId?:            string | null;
}

const AddLocationBody: React.FC<AddLocationBodyProps> = React.memo(({
  bindSubmitForm,
  countries,
  bindIsFormValid,
  isSubmitting,
  fetchRegionList,
  regions,
  timezones,
  type,
  fetchTimezoneList,
  crateLocation,
  userLocations,
  accountId,
  parent,
  currentLocation,
  editLocation,
  userId,
  fetchUserLocations,
}) => {
  const { t } = useTranslation();
  const account = accountId ? { id: accountId } : undefined;
  const currentType = currentLocation === null ? type : currentLocation.type;

  let validationData: any = {
    country: Yup.string().required(t('validation_required', { field: t('Country') })),
    timezone: Yup.string().required(t('validation_required', { field: t('Timezone') })),
    nickname: Yup.string().required(t('validation_required', { field: t('Name') })),
  };
  if (currentType ===  LocationType.UNIT) {
    validationData = {
      ...validationData,
      ...{
        state: Yup.string().required(t('validation_required', { field: t('State') })),
        postalCode: Yup.string().required(t('validation_required', { field: t('ZIP') })),
        city: Yup.string().required(t('validation_required', { field: t('City') })),
        address: Yup.string().required(t('validation_required', { field: t('Address Line 1') })),
      }
    }
  }
  const validationSchema = Yup.object().shape(validationData);

  const defaultParent = (fullOption = false): FloDropdownOption | string | undefined => {
    switch (true) {
      case type === LocationType.PROPERTY && parent?.type === LocationType.REGION:
      case type === LocationType.BUILDING && parent?.type === LocationType.REGION:
      case type === LocationType.UNIT && (parent?.type === LocationType.REGION || parent?.type === LocationType.PROPERTY || parent?.type === LocationType.BUILDING):
        return !fullOption ? parent?.id || '' : { value: parent?.id || '', label: parent?.nickname || parent?.address || '' };
      default:
        return !fullOption ? '' : undefined;
    }
  }
  const shouldPopulateParentData = (type === LocationType.UNIT && (parent?.type === LocationType.PROPERTY || parent?.type === LocationType.BUILDING));
  return (
    <>
      <Message />
      <Formik
        initialValues={{
          nickname: currentLocation?.nickname || '',
          country: currentLocation?.country || (shouldPopulateParentData && parent?.country) || '',
          state: currentLocation?.state || (shouldPopulateParentData && parent?.state) || '',
          postalCode: currentLocation?.postalCode || (shouldPopulateParentData && parent?.postalCode) || '',
          parent: defaultParent() as string,
          address: currentLocation?.address || (shouldPopulateParentData && parent?.address) || '',
          address2: currentLocation?.address2 || (shouldPopulateParentData && parent?.address2) || '',
          city: currentLocation?.city || (shouldPopulateParentData && parent?.city) || '',
          timezone: currentLocation?.timezone || (shouldPopulateParentData && parent?.timezone) || '',
        }}
        onSubmit={(values, actions) => {
          if (currentLocation === null) {
            crateLocation({
              parent: values.parent === '' ? undefined : { id: values.parent },
              nickname: values.nickname,
              address: values.address,
              address2: values.address2,
              city: values.city,
              timezone: values.timezone,
              country: values.country,
              postalCode: values.postalCode,
              state: values.state,
              type,
              children: [],
              account
            });
          } else {
            editLocation({
              id: currentLocation.id,
              nickname: values.nickname,
              address: values.address,
              address2: values.address2,
              city: values.city,
              timezone: values.timezone,
              country: values.country,
              postalCode: values.postalCode,
              state: values.state,
              type: currentLocation.type,
              children: [],
            })
          }
          actions.setSubmitting(false);
        }}
        validationSchema={validationSchema}
      >
        {({ values, handleBlur, handleChange, errors, touched, isValid, handleSubmit, submitForm, setFieldValue, setFieldTouched }) => {
          bindSubmitForm(submitForm);
          bindIsFormValid(isValid);

          const setField = (name: string, value: any) => {
            setFieldValue(name, value, true);
            setFieldTouched(name, true, false);
          };

          let locationOptions: FloDropdownOption[] = userLocations.rows?.map(r => ({ value: r.id || '', label: r.nickname || r.address || '' })) || [];
          const defParent = defaultParent(true) as  FloDropdownOption | undefined;
          if (defParent) {
            locationOptions.push(defParent);
            locationOptions = uniqBy(locationOptions, d => d.value);
          }

          return (
            <Form className={styles.form} onSubmit={handleSubmit}>
              <Row>
                <Col xs="12">
                  <FloFormGroup
                    controlId   = "nickname"
                    label       = {t('Name')}
                    touched     = {touched}
                    errors      = {errors}
                  >
                    <FloFormControl
                      type="text"
                      name="nickname"
                      placeholder={t('Name')}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.nickname}
                      disabled={isSubmitting}
                      isInvalid={!!(touched.nickname && errors.nickname)}
                    />
                  </FloFormGroup>
                </Col>
              </Row>

              <Row>
                <Col xs="12">
                  <FloFormGroup
                    controlId   = "country"
                    label       = {t('Country')}
                    touched     = {touched}
                    errors      = {errors}
                  >
                    <FloDropdown
                      id              = "country"
                      title           = {t('Country')}
                      values          = {values.country}
                      disabled        = {isSubmitting}
                      isLoading       = {countries === undefined}
                      loadingMessage  = {() => t('Loading...')}
                      full
                      options         = {countries?.map(c => ({ value: c.id, label: c.name })) || []}
                      onChange        = {e => {
                        setField('country', e.value);
                        if (values.timezone !== '') {
                          setField('timezone', '');
                        }
                        if (values.state !== '') {
                          setField('state', '');
                        }
                        fetchRegionList(e.value);
                        fetchTimezoneList(e.value, null);
                      }}
                      isInvalid       = {!!(touched.country && errors.country)}
                    />
                  </FloFormGroup>
                </Col>
              </Row>

              <Row>
                <Col xs="12">
                  <FloFormGroup
                    controlId   = "address"
                    label       = {t('Address Line 1')}
                    touched     = {touched}
                    errors      = {errors}
                  >
                    <FloFormControl
                      type="text"
                      name="address"
                      placeholder={t('Address Line 1')}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.address}
                      disabled={isSubmitting}
                      isInvalid={!!(touched.address && errors.address)}
                    />
                  </FloFormGroup>
                </Col>
              </Row>

              <Row>
                <Col xs="12">
                  <FloFormGroup
                    controlId   = "address2"
                    label       = {t('Address Line 2 (optional)')}
                    touched     = {touched}
                    errors      = {errors}
                  >
                    <FloFormControl
                      type="text"
                      name="address2"
                      placeholder={t('Address Line 2')}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.address2}
                      disabled={isSubmitting}
                      isInvalid={!!(touched.address2 && errors.address2)}
                    />
                  </FloFormGroup>
                </Col>
              </Row>

              <Row>
                <Col xs="6">
                  <FloFormGroup
                    controlId   = "state"
                    label       = {t('State')}
                    touched     = {touched}
                    errors      = {errors}
                  >
                    <FloDropdown
                      id              = "state"
                      title           = {t('State')}
                      values          = {values.state}
                      disabled        = {isSubmitting}
                      isLoading       = {regions === undefined}
                      loadingMessage  = {() => t('Loading...')}
                      full
                      options         = {regions?.map(r => ({ value: r.id, label: r.name })) || []}
                      onChange        = {e => {
                        setField('state', e.value);
                        if (values.timezone !== '') {
                          setField('timezone', '');
                        }
                        fetchTimezoneList(values.country, e.value);
                      }}
                      isInvalid       = {!!(touched.state && errors.state)}
                    />
                  </FloFormGroup>
                </Col>
                <Col xs="6">
                  <FloFormGroup
                    controlId   = "city"
                    label       = {t('City')}
                    touched     = {touched}
                    errors      = {errors}
                  >
                    <FloFormControl
                      type="text"
                      name="city"
                      placeholder={t('City')}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.city}
                      disabled={isSubmitting}
                      isInvalid={!!(touched.city && errors.city)}
                    />
                  </FloFormGroup>
                </Col>
              </Row>

              <Row>
                <Col xs="6">
                  <FloFormGroup
                    controlId   = "timezone"
                    label       = {t('Timezone')}
                    touched     = {touched}
                    errors      = {errors}
                  >
                    <FloDropdown
                      id              = "timezone"
                      title           = {t('Timezone')}
                      values          = {values.timezone}
                      disabled        = {isSubmitting}
                      isLoading       = {timezones === undefined}
                      loadingMessage  = {() => t('Loading...')}
                      full
                      options         = {timezones?.map(r => ({ value: r.id, label: r.name })) || []}
                      onChange        = {e => setField('timezone', e.value)}
                      isInvalid       = {!!(touched.timezone && errors.timezone)}
                    />
                  </FloFormGroup>
                </Col>
                <Col xs="6">
                  <FloFormGroup
                    controlId   = "postalCode"
                    label       = {t('ZIP')}
                    touched     = {touched}
                    errors      = {errors}
                  >
                    <FloFormControl
                      type="text"
                      name="postalCode"
                      placeholder={t('ZIP')}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.postalCode}
                      disabled={isSubmitting}
                      isInvalid={!!(touched.postalCode && errors.postalCode)}
                    />
                  </FloFormGroup>
                </Col>
              </Row>

              { type !== LocationType.REGION && currentLocation === null &&
                <Row>
                  <Col xs="12">
                    <FloFormGroup
                      controlId   = "parent"
                      label       = {t(type === LocationType.UNIT ? 'add-to-property-community' : 'add-to-community')}
                      touched     = {touched}
                      errors      = {errors}
                    >
                      <FloDropdown
                        id                    = "parent"
                        title                 = {t(type === LocationType.UNIT ? 'add-to-property-community' : 'add-to-community')}
                        values                = {values.parent}
                        disabled              = {isSubmitting}
                        isLoading             = {userLocations.isLoading}
                        loadingMessage        = {() => t('Loading...')}
                        full
                        disableMemoryFilter
                        options               = {locationOptions}
                        onChange              = {e => setField('parent', e.value)}
                        isInvalid             = {!!(touched.parent && errors.parent)}
                        page                  = {userLocations.page}
                        hasMore               = {(userLocations.total || 0) > (userLocations.rows?.length || 0)}
                        onLoadOptions         = {(search: string | undefined, page: number) => {
                          if (page === 1) {
                            setField('parent', '');
                          }
                          fetchUserLocations(
                            userId as string,
                            (type === LocationType.PROPERTY || type === LocationType.BUILDING) ? [LocationType.REGION] : [LocationType.PROPERTY, LocationType.BUILDING, LocationType.REGION],
                            page,
                            search
                          );
                        }}
                      />
                    </FloFormGroup>
                  </Col>
                </Row>
              }
            </Form>
          )
        }}
      </Formik>
    </>
  )
})

const getTitleKey = (type: LocationType, current: Location | null): string => {
  const isNew = current === null;
  const currentType = current === null ? type : current.type;
  switch(currentType) {
    case LocationType.UNIT:
      return `${isNew ? 'new' : 'edit'}-location-unit-title`;
    case LocationType.REGION:
      return `${isNew ? 'new' : 'edit'}-location-community-title`;
    case LocationType.PROPERTY:
      return `${isNew ? 'new' : 'edit'}-location-property-title`;
    case LocationType.BUILDING:
      return `${isNew ? 'new' : 'edit'}-location-building-title`;
  }
}

export interface AddLocationModalProps extends FloComponentProps {
  show:               boolean;
  onHide:             () => void;
  countries?:         Country[];
  regions?:           Region[];
  timezones?:         Timezone[];
  isSubmitting:       boolean;
  accountId?:         string;
  fetchRegionList:    (countryId: string) => void;
  type:               LocationType;
  fetchTimezoneList:  (countryId: string, stateId: string | null) => void;
  crateLocation:      (location: Location) => void;
  editLocation:       (location: Location) => void;
  userLocations:      ListView<Location>;
  parent:             Location | null;
  currentLocation:    Location | null;
  fetchUserLocations: (userId: string, types: LocationType[], page: number, search?: string) => void;
  userId?:            string | null;
}

export const AddLocationModal: React.FC<AddLocationModalProps> = React.memo(({
  type,
  timezones,
  isSubmitting,
  fetchRegionList,
  countries,
  regions,
  accountId,
  fetchTimezoneList,
  crateLocation,
  userLocations,
  parent,
  currentLocation,
  editLocation,
  fetchUserLocations,
  show,
  userId,
  ...rest
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (currentLocation !== null) {
      fetchRegionList(currentLocation.country);
      fetchTimezoneList(currentLocation.country, currentLocation.state || null);
    }
  }, [currentLocation]);

  useEffect(() => {
    if (show && userId && type !== LocationType.REGION && currentLocation === null) {
      fetchUserLocations(userId, (type === LocationType.PROPERTY || type === LocationType.BUILDING) ? [LocationType.REGION] : [LocationType.PROPERTY, LocationType.BUILDING, LocationType.REGION], 1);
    }
  }, [show]);

  useEffect(() => {
    if (show && parent && type === LocationType.UNIT && (parent.type === LocationType.PROPERTY || parent.type === LocationType.BUILDING) && currentLocation === null) {
      fetchRegionList(parent.country);
      fetchTimezoneList(parent.country, parent.state || null);
    }
  }, [show]);

  return (
    <FloModal
      {...rest}
      show            = {show}
      name            = "new-location"
      title           = {t(getTitleKey(type, currentLocation))}
      isSubmitting    = {isSubmitting}
      disableButtons  = {isSubmitting}
      body            = {(props) => <AddLocationBody
                                      {...props}
                                      isSubmitting        = {isSubmitting}
                                      accountId           = {accountId}
                                      countries           = {countries}
                                      fetchRegionList     = {fetchRegionList}
                                      regions             = {regions}
                                      type                = {type}
                                      fetchTimezoneList   = {fetchTimezoneList}
                                      timezones           = {timezones}
                                      crateLocation       = {crateLocation}
                                      userLocations       = {userLocations}
                                      parent              = {parent}
                                      currentLocation     = {currentLocation}
                                      editLocation        = {editLocation}
                                      userId              = {userId}
                                      fetchUserLocations  = {fetchUserLocations}
                                    />
                                  }
    />
  );
})

export default AddLocationModal;
